export const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * srcからdestに、keyで指定したプロパティのみをコピーする
 * @param {T} dest コピー先
 * @param {T | null | undefined} src コピー元 nullやundefinedの場合は何もコピーしない
 * @param {ReadonlyArray<keyof T>} keys コピー対象のプロパティ
 */
export const copySpecifiedProperties = <T>(dest: T, src: T | null | undefined, keys: ReadonlyArray<keyof T>): void => {
    if (src) {
        for (let key of keys) {
            dest[key] = src[key];
        }
    }
};

/**
 * 2つのオブジェクトのkeysで指定したプロパティを比較し、全て一致していればtrueを返す
 * @param {T} val1 比較したいオブジェクト
 * @param {T} val2 比較したいオブジェクト
 * @param {ReadonlyArray<keyof T>} keys 比較対象のプロパティ
 * @returns {boolean} 全て一致していればtrue 一つ以上異なっていればfalse
 */
export const everySpecifiedPropertiesSame = <T>(val1: T, val2: T, keys: ReadonlyArray<keyof T>): boolean => {
    for (let key of keys) {
        if (val1[key] != val2[key]) {
            return false;
        }
    }
    return true;
};

/**
 * 2つのオブジェクトのkeysで指定したプロパティを比較し、一つでも異なっていればtrueを返す
 * @param {T} val1 比較したいオブジェクト
 * @param {T} val2 比較したいオブジェクト
 * @param {ReadonlyArray<keyof T>} keys 比較対象のプロパティ
 * @returns {boolean} 全て一致していればfalse 一つ以上異なっていればtrue
 */
export const someSpecifiedPropertiesDifferent = <T>(val1: T, val2: T, keys: ReadonlyArray<keyof T>): boolean => {
    for (let key of keys) {
        if (val1[key] != val2[key]) {
            return true;
        }
    }
    return false;
};

/**
 * 2つのオブジェクトを比較し、変更があったプロパティのみを抽出して、新しいオブジェクトに入れて返す
 * @param {T} newVal 変更後のオブジェクト
 * @param {T | null | undefined} oldVal 変更前のオブジェクト nullやundefinedの場合は全て変更されたものとする
 * @param {ReadonlyArray<keyof T>} keys 比較対象のプロパティ
 * @returns {Partial<T>} 抽出した新しいオブジェクト
 */
export const extractChangedProperties = <T>(
    newVal: T,
    oldVal: T | null | undefined,
    keys: ReadonlyArray<keyof T>
): Partial<T> => {
    let extracted: Partial<T> = {};

    for (let key of keys) {
        if (oldVal) {
            if (newVal[key] !== oldVal[key]) {
                extracted[key] = newVal[key];
            }
        } else {
            extracted[key] = newVal[key];
        }
    }

    return extracted;
};

/**
 * オブジェクトのシャローコピー
 * @param {T} oldVal コピー元
 * @returns {T} 新しいオブジェクト
 */
export const shallowCopy = <T>(oldVal: T): T => {
    let newVal = {} as T;

    for (const key in oldVal) {
        newVal[key] = oldVal[key];
    }

    return newVal;
};

export const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
        const fileReader = new FileReader();

        fileReader.onloadend = () => resolve((fileReader.result as string).split(',')[1]);
        fileReader.onerror = e => reject(e);

        fileReader.readAsDataURL(file);
    });
};

export const readBlobAsJson = (blob: Blob): Promise<any> => {
    return new Promise<any>((resolve, reject) => {
        const fileReader = new FileReader();

        fileReader.onloadend = () => {
            try {
                resolve(JSON.parse(fileReader.result as string));
            } catch (e) {
                reject(e);
            }
        };
        fileReader.onerror = e => reject(e);

        fileReader.readAsText(blob);
    });
};

/**
 * 文字列のUTF-8でのバイト数を取得する
 * @param {string} str 文字列
 * @returns {number} バイト数
 */
export const stringByteSize = (str: string): number => {
    return new Blob([str], { type: 'text/plain' }).size;
};

/**
 * context.ts にある {text_key; value;} の配列から、valueで検索してtext_keyを取得する
 * 見つからなかった場合は not_found 引数で指定した文字列を返す
 */
export const findTextKeyByValue = <T>(
    list: { text_key: string; value: T }[],
    by: T,
    not_found: string = ''
): string => {
    return list.find(i => i.value === by)?.text_key ?? not_found;
};

interface childrenwise {
    children?: Array<childrenwise>;
}
export const treeFilter = <T extends childrenwise>(src: Array<T>, callback: (element: T) => boolean): Array<T> => {
    return src
        .map(oldNode => {
            let newNode = shallowCopy(oldNode);

            if (oldNode.children) {
                newNode.children = treeFilter(oldNode.children, callback);
            }

            return newNode;
        })
        .filter(node => {
            return callback(node);
        });
};

/**
 * 数値であるか判定
 * MDNのNumber.isIntegerはIEではサポートできないため独自にオブジェクトを作成
 * @param {number} number 対象の数値
 * @returns {boolean} True: 数値である False: 数値以外である
 */
export const isNumber = (number: number): boolean => {
    return typeof number === 'number' && number != null && number != undefined;
};

/**
 * 配列同士の比較
 * 両方とも完全に同じであるか
 * array1とarray2が同じ型でない場合はエラーが発生する
 * @param {Array<T extends number | string>} array1
 * @param {Array<T extends number | string>} array2
 * @returns {boolean} True: すべて同じ場合, False: それ以外の場合
 */
export const isEqualArray = <T extends number | string>(array1: Array<T>, array2: Array<T>): boolean => {
    return array1.every(item => array2.includes(item)) && array2.every(item => array1.includes(item));
};

/**
 * 空の判定
 * @param {T} value 判定したい値
 * @returns {boolean} True: 値が空の場合, False: 値が空でない場合
 */
export const isEmpty = <T extends number | string | object | null | undefined>(value: T): boolean => {
    if (value == null) return true;
    switch (typeof value) {
        case 'object':
            if (Array.isArray(value)) {
                return value.length == 0;
            } else {
                if (Object.keys(value).length > 0 || Object.getOwnPropertySymbols(value).length > 0) return false;
                return true;
            }
        case 'string':
            return value === '';
        case 'number':
            return value === 0;
        case 'undefined':
            return true;
        default:
            return false;
    }
};
